import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CdkConnectedOverlay, ViewportRuler } from '@angular/cdk/overlay';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    input,
    model,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    output,
    signal,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ModalService } from '@common/components/modal/modal.service';
import { DateTime } from '@common/data-models/complex-types/date-time';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { AttachmentService } from '@common/services/attachment-service';
import { BasicContentService } from '@common/services/basic-content-service';
import { ExternalActionsService } from '@common/services/external-actions-service';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { LayoutState } from '@common/services/layout-state';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { selectViewStateCustomCardItems, selectViewStateFilterState, selectViewStateSearchString } from '@common/store/navigation/selectors/viewstate.selectors';
import { Store } from '@ngrx/store';
import { PlaybookService } from '@playbook/services/playbook-service';
import { Address } from '@src/suite/common/data-models/complex-types/address';
import { CARD_WIDTH_LOWER_BREAKPOINTS, PST_PREFIX } from '@src/suite/common/enums/constants';
import { CardViewType, FieldDefValidations } from '@src/suite/common/enums/enums';
import { HighlightPipe } from '@src/suite/common/pipes/highlight';
import { selectClientTimeZone, selectPersonalTimeZone } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { RecordHttpActions, TopicRecordActions } from '@src/suite/common/store/records/records.actions';
import { selectStoryTemplates } from '@src/suite/common/store/records/records.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { getDataTransferString, getTopicSecurityPrivsWrite } from '@src/suite/common/utils/record-utils';
import { AppService } from '@suite/app.service';
import { DraggableEnpsComponent } from '@suite/common/components/base/draggable-enps-component';
import { GroupKey } from '@suite/common/data-models/search-results/group-key';
import { getItemString } from '@suite/common/utils/util';

@Component({
    selector: 'st-topic-item',
    templateUrl: './st-topic-item.component.html',
    styleUrls: ['./st-topic-item.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-topic-item',
    },
})
export class StTopicItemComponent extends DraggableEnpsComponent implements OnDestroy, OnInit, AfterViewInit {
    private store = inject(Store);
    highlightPipe = inject(HighlightPipe);
    viewportRuler = inject(ViewportRuler);
    elmRef = inject(ElementRef);

    public readonly FieldDefValidations = FieldDefValidations;
    pstFieldNames = PSTFieldNames;
    CardViewTypes = CardViewType;
    isAssignmentsPanelOpen = false;
    attachments: any;
    public readonly getItemString = getItemString;
    public itemStringCache = new Map<string, string>();

    viewStateCustomCardItems = this.store.selectSignal(selectViewStateCustomCardItems);
    viewStateFilterState = this.store.selectSignal(selectViewStateFilterState);
    i18n = this.store.selectSignal(selectI18N);
    searchString = this.store.selectSignal(selectViewStateSearchString);
    storyTemplates = this.store.selectSignal(selectStoryTemplates);
    personalTimeZone = this.store.selectSignal(selectPersonalTimeZone);
    clientTimeZone = this.store.selectSignal(selectClientTimeZone);

    data = model<StorytellingTopicRecord>(null);
    isReadOnly = input(false);
    preview = input(false);
    isPublication = input(false);
    customCardItems = input<FieldDef[]>([]);
    currentGroup = input<GroupKey>(null);
    inSidecarOpenedMode = input(false, {
        transform: coerceBooleanProperty,
    });

    inView = signal(false);
    cardViewType = signal(CardViewType.LARGE);

    customCardItemsRet = computed(() => {
        if (this.customCardItems()?.length > 0 && this.preview()) {
            return this.customCardItems();
        }
        return this.viewStateCustomCardItems();
    });
    storyItemClicked = output<StorytellingStoryRecord>();

    @Output() public dropEmitter: EventEmitter<any> = new EventEmitter();
    @Output() public topicItemClicked: EventEmitter<void> = new EventEmitter();
    @Output() public assignmentItemClicked: EventEmitter<StorytellingAssignmentRecord> = new EventEmitter();

    @Output() public deletePublicationEmitter: EventEmitter<any> = new EventEmitter();

    @ViewChild('assignmentsOverlayEl', { read: CdkConnectedOverlay }) assignmentsOverlayEl: CdkConnectedOverlay;

    @HostListener('click')
    public onClick() {
        if (this.cardDisabled()) {
            return;
        }
        if (this.cardViewType() === CardViewType.SMALL) {
            this.topicItemClicked.emit();
        }
    }

    currentTimeZone = computed(() => {
        return this.personalTimeZone() ? this.personalTimeZone() : this.clientTimeZone();
    });

    topicTypes = computed(() => {
        return this.data()?.PSTTopicType?.sort();
    });

    timeUpdatedLabel = computed(() => {
        return `${this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebChangedAgoLabel))()} ${this.data()?.ModTime?.moment?.fromNow()}`;
    });

    updatedBy = computed(() => {
        return `${this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebChangedByLabel))()} ${this.globalTablesService.Staff[this?.data()?.ModBy]?.DisplayName}`;
    });

    timeUpdatedByLabel = computed(() => {
        return `${this.timeUpdatedLabel()} ${this.updatedBy()}`;
    });

    childrenCount = computed(() => {
        return this.data()?.children.length;
    });

    extraAssignmentsCount = computed(() => {
        return this.data()?.children?.length - 4;
    });

    shouldShowExtraAssignments = computed(() => {
        return this.data()?.children?.length > 5;
    });

    topicEditable = computed(() => {
        return !this.isReadOnly() && getTopicSecurityPrivsWrite(this.data()) && !this.data()?.Path?.isWasteBinPath;
    });

    cardDisabled = computed(() => {
        return this.isReadOnly() || !this.topicEditable() || this.preview() || this.data()?.Path?.isWasteBinPath;
    });

    get assignmentTypes(): { id: string; title: string }[] {
        return this.globalTablesService.getListItemsForField(PSTFieldNames.DescriptionType);
    }

    get showFullCard(): boolean {
        return !this.visualStateService.isLessThanMedium;
    }

    maxTagsToShow = computed(() => {
        if (this.visualStateService.isLessThanLarge) {
            return 4;
        }
        if (this.visualStateService.isLessThanXLarge) {
            return 6;
        }
        if (this.viewStateFilterState() && this.appService.sideBarExpanded()) {
            return 6;
        }
        if (this.viewStateFilterState() || this.appService.sideBarExpanded()) {
            return 9;
        }
        return 12;
    });

    displayOpenActionButtonHoverTitle = computed(() => {
        return this.globalTablesService.getDisplayOpenActionButtonHoverTitle(this.inSidecarOpenedMode())();
    });

    get isRTL() {
        return this.globalTablesService.getRtl();
    }

    get maxTypesToShow(): number {
        if (this.visualStateService.isLessThanLarge) {
            return 1;
        }
        if (this.visualStateService.isLessThanXLarge) {
            return 3;
        }
        return 3;
    }

    get enforceRequiredFieldsOnCreate(): boolean {
        return this.globalTablesService.EnforceRequiredFieldsOnCreate;
    }

    private containerResizeObserver: ResizeObserver;

    constructor(
        public appService: AppService,
        public basicContentService: BasicContentService,
        public nomWebServicesJson: NomWebServicesJson,
        public playbookService: PlaybookService,
        public ngZone: NgZone,
        public changeDetectorRef: ChangeDetectorRef,
        public languageSettings: LanguageSettings,
        public modalService: ModalService,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        public navigationService: NavigationService,
        public externalActionsService: ExternalActionsService,
        public viewStateService: LayoutState,
        protected dialog: MatDialog,
        protected attachmentService: AttachmentService
    ) {
        super();
        effect(() => {
            if (this.data()) {
                this.itemStringCache.clear();
            }
        });
    }

    ngOnInit() {
        setTimeout(() => {
            this.inView.set(true);
        }, 0);
    }

    ngAfterViewInit() {
        this.containerResizeObserver = new ResizeObserver(entries => {
            const width = entries[0].contentRect.width;
            if (width < CARD_WIDTH_LOWER_BREAKPOINTS.medium) {
                this.cardViewType.set(CardViewType.SMALL);
            } else if (width < CARD_WIDTH_LOWER_BREAKPOINTS.large) {
                this.cardViewType.set(CardViewType.MEDIUM);
            } else {
                this.cardViewType.set(CardViewType.LARGE);
            }
        });

        this.containerResizeObserver.observe(this.elmRef.nativeElement);
    }

    ngOnDestroy(): void {
        this.containerResizeObserver.unobserve(this.elmRef.nativeElement);
        this.containerResizeObserver.disconnect();
    }

    dragStartHandler(event: DragEvent) {
        event.dataTransfer.setData('text/plain', PST_PREFIX + JSON.stringify(getDataTransferString(this.data())));
        event.stopPropagation();
    }

    async onItemDrop(data: any) {
        // TODO THIS NEEDS TO GO TO STORE
        // if (data instanceof FileList) {
        const attachments = [];
        for (let i = 0; i < data?.length; i++) {
            const shouldProceed = await this.appService.shouldProceedAttachment(data[i].name, data[i].size);
            if (!shouldProceed) return;
            const attachment = data[i];
            attachments.push({ file: attachment, finished: false, name: attachment.name });
        }
        const base64Files = await this.appService.readUrl(attachments);
        for (const file of base64Files) {
            try {
                file.ExternalFiles = this.data()?.ExternalFiles;
                const res = await this.appService.attachmentService.createAttachment(this.data()?.Database, this.data()?.Path.fullPath, this.data()?.Guid, false, file, this.data());
                if (res && res.status) {
                    const dataToSend = {
                        ExternalFiles: this.data()?.ExternalFiles,
                        Type: this.data()?.Type,
                    };
                    await this.appService.basicContentService.updateBasicContentV2<StorytellingTopicRecord | StorytellingAssignmentRecord>(
                        this.data()?.Database,
                        this.data()?.Path.fullPath,
                        this.data()?.Guid,
                        false,
                        dataToSend,
                        false,
                        this.data()
                    );
                }
            } catch (ex) {
                console.log(ex);
            }
        }
    }

    // if (data instanceof DropEvent) {
    //     this.onDrop.emit({ sourceGuid: data?.dragData?.guid, targetGuid: this.data?.guid, sourceGroup: data?.dragData?.group, targetGroup: this.currentGroup });
    // }

    getInnerProp(item: FieldDef) {
        const val = this.data()?.[item.ID.split('-')[0]];
        if (val) return val[item.ComplexPropInnerId];
        return '';
    }

    async createAssignment(type: string) {
        this.store.dispatch(PlaybookApiActions.createAssignment({ topic: this.data(), assignmentType: type }));
    }

    async createStory(event: { templateProgramId: string; templateGuids: string[] }) {
        this.store.dispatch(PlaybookApiActions.createStoryFromTemplate({ topic: this.data(), templateProgramId: event.templateProgramId, templateGuids: event.templateGuids }));
    }

    updateTopic(value: any, fieldId: string) {
        if (this.isReadOnly()) return;
        const fieldDef = [{ value, key: fieldId }];
        // TODO: not cool to have this title logic here
        if (fieldId === PSTFieldNames.Title) {
            this.data().Title = value.trim();
            this.data().Slug = value.trim();
            fieldDef.push({ value: value, key: PSTFieldNames.Slug });
        }
        if (fieldId === PSTFieldNames.PSTTopicEventStartDate) {
            const castedVal = value as DateTime;
            if (castedVal?.get() > this.data()?.[PSTFieldNames.PSTTopicEventEndDate]?.get()) {
                fieldDef.push({ value: new DateTime(castedVal?.get()), key: PSTFieldNames.PSTTopicEventEndDate });
            }
        }
        this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: this.data(), fieldsToUpdate: fieldDef }));
    }

    timezoneChanged(fieldId: string, event: DateTime) {
        this.data.update(val => {
            val[fieldId] = event;
            return val;
        });
        this.store.dispatch(TopicRecordActions.updateTopics({ records: [this.data()] }));
    }

    updateTopicAddresSubField(value: any, field: FieldDef, subFieldId: string) {
        if (this.isReadOnly()) return;
        const fieldId = field?.ID?.split('-')?.[0];
        if (fieldId) {
            const fieldDef = this.globalTablesService?.FieldDefsDict[fieldId];
            if (fieldDef?.Validation.toUpperCase() === FieldDefValidations.ADDRESS) {
                const address = this.data()?.[fieldId] || new Address();
                address[subFieldId] = value;
                this.updateTopic(address, fieldId);
            }
        }
    }

    onMouseOutAssignments(event: MouseEvent) {
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-assignments-overlay')) {
            this.isAssignmentsPanelOpen = false;
        }
    }

    assignmentsPanelAttached() {
        this.panelAttached(this.assignmentsOverlayEl);
    }

    private panelAttached(overlayEl: CdkConnectedOverlay = null) {
        const overlayElement = overlayEl?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener('mouseleave', () => {
                this.isAssignmentsPanelOpen = false;
            });
        }
    }

    openAssignmentsOverlay() {
        if (this.cardDisabled()) return;
        this.isAssignmentsPanelOpen = true;
    }

    getMinDate(id: string): DateTime {
        if (id === PSTFieldNames.PSTTopicEventEndDate) {
            return this.data()?.[PSTFieldNames.PSTTopicEventStartDate];
        }
    }

    focusDuplicate() {
        this.topicItemClicked.emit();
    }

    deletePublication(event: any) {
        event.stopPropagation();
        this.deletePublicationEmitter.emit();
    }

    openAssignment(assignment: StorytellingAssignmentRecord) {
        this.assignmentItemClicked.emit(assignment);
    }

    openStory(story: StorytellingStoryRecord) {
        this.storyItemClicked.emit(story);
    }
}
